import { FieldModel, ResourceDetail } from '@cibo/core'
import {
  FieldNameCell,
  SaveDetailRequest,
  useFields,
  useSaveBulkDetails,
  useSaveDetail,
} from '@cibo/landmanager'
import { DataGridPro, Markdown, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Box, MenuItem, Select, Stack, Typography, styled } from '@mui/material'
import { GridRenderCellParams, GridRowHeightReturnValue, useGridApiRef } from '@mui/x-data-grid-pro'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailStatus } from '../../DetailEditing/Rollups/types'
import { getDetailStatuses } from '../../DetailEditing/Rollups/utils'
import { countUniqueValues } from '../../DetailEditing/utils'

const StyledDataGridPro = styled(DataGridPro)(() => ({
  '.MuiDataGrid-cell': {
    padding: 0,
    position: 'relative',
  },
  '.MuiSelect': { boxShadow: 'none' },
  '.MuiDataGrid-pinnedRows': {
    backgroundColor: 'unset',
    boxShadow: 'unset',
  },
}))

export type SelectRollupEditorProps<
  RDT extends ResourceDetail = ResourceDetail<any, any>,
  O extends readonly string[] = readonly string[]
> = {
  detailKey: keyof RDT['input'] | keyof RDT['result']
  ns: string
  options: O
} & ResourceDetailFeatureTaskEditorProps<RDT>

export const SelectRollupEditor = <RDT extends ResourceDetail, O extends readonly string[]>({
  options,
  detailRequirements: [{ traitId, year }],
  ns,
  detailKey,
  onError,
  onSuccess,
  onUpdating,
  resourceIds,
  userRole,
  ownerName,
}: SelectRollupEditorProps<RDT, O>) => {
  const { t } = useTranslation('@cibo/landmanager/SelectRollupEditor')
  const { data: fieldModels, dataUpdatedAt: fieldModelsDataUpdatedAt } = useFields(resourceIds)
  const apiRef = useGridApiRef()
  const [rollupValue, setRollupValue] = useState<ResourceDetail>()
  const [rollupDisabled, setRollupDisabled] = useState(false)
  const { mutateAsync: saveBulkDetails } = useSaveBulkDetails()
  const saveDetail = useSaveDetail()

  const detailStatuses =
    traitId && fieldModels && year && getDetailStatuses(traitId, fieldModels, [year])

  useEffect(() => {
    setRollupDisabled(
      !!fieldModels?.some(field => field.resolveStandingDetail(traitId, year)?.immutable)
    )
    const uniqueYearValues =
      fieldModels && countUniqueValues({ fieldModels: fieldModels, traitId, year })
    if (uniqueYearValues === 1 && detailStatuses) {
      const currentValueOfRollup = detailStatuses.find(
        ({ year: detailYear }) => year === detailYear
      )?.detail
      setRollupValue(currentValueOfRollup)
    } else if (!uniqueYearValues || uniqueYearValues > 1) {
      setRollupValue({ result: { [detailKey]: '' }, traitId })
    }
  }, [fieldModelsDataUpdatedAt, year])

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('field', { ns: '@cibo/landmanager/SelectRollupEditor' }),
        hideable: false,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<string, FieldModel>) => (
          <Box sx={{ marginLeft: 2, py: 2 }}>
            {params.row.resourceId === 'rollupRow' ? (
              <Typography sx={{ fontWeight: 'bold', my: 2 }}>
                {t('answerForAllFields', { ns: '@cibo/landmanager/SelectRollupEditor' })}
              </Typography>
            ) : (
              <FieldNameCell {...params} />
            )}
          </Box>
        ),
      },
      {
        field: 'answer',
        headerName: t('answer', { ns, defaultValue: null }) ?? t('answer'),
        flex: 1,
        hideable: false,
        disableColumnMenu: true,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<string, FieldModel>) => {
          const isRollup = row.resourceId === 'rollupRow'
          return (
            <Select
              value={
                (isRollup
                  ? rollupValue && rollupValue.result[detailKey]
                  : row.resolveStandingDetail(traitId, year)?.result[detailKey]) || ''
              }
              onChange={event =>
                commitChanges({
                  resourceId: row.resourceId,
                  details: [{ traitId, input: { [detailKey]: event?.target.value }, year } as RDT],
                })
              }
              placeholder={t('select')}
              data-testid="selectRollupDropdown"
              variant="outlined"
              sx={{ width: '100%' }}
              disabled={
                isRollup ? rollupDisabled : row.resolveStandingDetail(traitId, year)?.immutable
              }
            >
              {options.map(option => (
                <MenuItem key={option} value={option}>
                  {t(`option_${option}`, { ns })}
                </MenuItem>
              ))}
            </Select>
          )
        },
      },
    ]
  }, [rollupValue, fieldModelsDataUpdatedAt])

  const commitChanges = useCallback(
    (request: SaveDetailRequest<RDT>) => {
      onUpdating?.()
      const { details, resourceId } = request
      if (!detailStatuses) return Promise.resolve()
      if (resourceId === 'rollupRow') {
        const resourcesToUpdate = detailStatuses
          .map(resource => {
            return details[0]?.input &&
              details[0]?.input[detailKey as string] !== resource.detail?.result[detailKey]
              ? resource
              : undefined
          })
          .filter(a => !!a)
        const resourceIds = resourcesToUpdate.map(
          resource => (resource as Omit<DetailStatus, 'matchesRollup'>).resourceId
        )
        return saveBulkDetails({
          resourceIds,
          details,
        })
          .then(() => onSuccess?.())
          .catch(e => onError?.(e))
      } else {
        return saveDetail
          .mutateAsync(request)
          .then(() => onSuccess?.())
          .catch(e => onError?.(e))
      }
    },
    [detailStatuses]
  )

  const getRowHeight = useCallback((): GridRowHeightReturnValue => 'auto', [])

  return (
    <Stack spacing={3} flex={1}>
      <Markdown
        overrides={{
          em: {
            component: ({ children }: { children: ReactNode }) => (
              <Typography variant="body2" color="text.secondary" component="span">
                {children}
              </Typography>
            ),
          },
        }}
      >
        {[
          t('question', {
            context: userRole,
            name: ownerName,
            ns,
            count: fieldModels?.length,
          }),
          t('optionsExplainer', { ns, defaultValue: null }),
        ]
          .filter(Boolean)
          .join('<br/><br/>')}
      </Markdown>
      <Stack>
        <StyledDataGridPro
          experimentalFeatures={{ rowPinning: true }}
          pinnedRows={
            fieldModels?.length && fieldModels.length > 1
              ? {
                  top: [
                    {
                      resourceId: 'rollupRow',
                      isRollup: true,
                      resolveStandingDetail: () => rollupValue,
                      isDetailAbsentOrIncomplete: () => false,
                    },
                  ],
                }
              : {}
          }
          apiRef={apiRef}
          rows={fieldModels ?? []}
          getRowHeight={getRowHeight}
          getRowId={a => a.resourceId || 'rollup'}
          columns={columns}
          autoHeight
          hideCellFocus
        />
      </Stack>
    </Stack>
  )
}
